import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'

import { IError } from '../base/error'

export interface RequestOptions {
  baseURL: string
  headers?: Record<string, unknown>
}

export class Request {
  private readonly api: AxiosInstance

  constructor(options: RequestOptions) {
    this.api = axios.create({
      baseURL: options.baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })
  }

  public async patch<T, U = T>(
    url: string,
    data: any,
    configs?: AxiosRequestConfig,
  ): Promise<Response<T, U>> {
    const response = new Response<T, U>()
    return new Promise((resolve, reject) => {
      this.api
        .patch<T>(url, data, configs)
        .then((result) => {
          response.result = result.data
          response.status = result.status
          resolve(response)
        })
        .catch((error: AxiosError) => {
          if (error && error.response) {
            response.error = error.response.data
            reject(response)
          }
        })
    })
  }

  public async delete<T, U = T>(
    url: string,
    configs?: AxiosRequestConfig,
  ): Promise<Response<T, U>> {
    const response = new Response<T, U>()
    return new Promise((resolve, reject) => {
      this.api
        .delete<T>(url, configs)
        .then((result) => {
          response.result = result.data
          response.status = result.status
          resolve(response)
        })
        .catch((error: AxiosError) => {
          if (error && error.response) {
            response.error = error.response.data
            reject(response)
          }
        })
    })
  }

  public async post<T, U = T>(
    url: string,
    data: any,
    configs?: AxiosRequestConfig,
  ): Promise<Response<T, U>> {
    const response = new Response<T, U>()
    return new Promise((resolve, reject) => {
      this.api
        .post<T>(url, data, configs)
        .then((result) => {
          response.result = result.data
          response.status = result.status
          resolve(response)
        })
        .catch((error: AxiosError) => {
          if (error && error.response) {
            response.error = error.response.data
            reject(response)
          }
        })
    })
  }

  public async put<T, U = T>(
    url: string,
    data: any,
    configs?: AxiosRequestConfig,
  ): Promise<Response<T, U>> {
    const response = new Response<T, U>()
    return new Promise((resolve, reject) => {
      this.api
        .put<T>(url, data, configs)
        .then((result) => {
          response.result = result.data
          response.status = result.status
          resolve(response)
        })
        .catch((error: AxiosError) => {
          if (error && error.response) {
            response.error = error.response.data
            reject(response)
          }
        })
    })
  }

  public async get<T, U = T>(
    url: string,
    configs?: AxiosRequestConfig,
  ): Promise<Response<T, U>> {
    const response = new Response<T, U>()
    return new Promise((resolve, reject) => {
      this.api
        .get<T>(url, configs)
        .then((result) => {
          response.result = result.data
          response.status = result.status
          resolve(response)
        })
        .catch((error: AxiosError) => {
          if (error && error.response) {
            response.error = error.response.data
            reject(response)
          }
        })
    })
  }
}

export class Response<T, E = IError> {
  result?: T
  error?: E | unknown
  status?: number

  constructor(status?: number, result?: T, error?: E) {
    this.status = status
    this.result = result
    this.error = error
  }

  static resolve<T>(status: number, result: T) {
    return new Response<T, never>(status, result)
  }

  static error<E>(status: number, error: E) {
    return new Response<never, E>(status, undefined, error)
  }

  success(): boolean {
    return String(this.status)[0] === '2'
  }

  failed(): boolean {
    const hundredsErrorCode = ['4', '5']
    return hundredsErrorCode.includes(String(this.status)[0])
  }
}
